import type { TopicContent } from '../types';
import { ELEMENTS_28_SVG_MARKUP } from '../assets/elements-of-creation-svg';

const content: TopicContent = {
    blocks: [
        {
            type: 'diagram',
            diagram: {
                svgMarkup: ELEMENTS_28_SVG_MARKUP,
                zoomable: true,
            },
        },
        {
            type: 'text',
            content:
                'This diagram visually maps Lord Kapiladeva\'s ontology of the 28 elements of creation as described in Śrīmad-Bhāgavatam, 3rd Canto, 26th Chapter.',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'The 28 Elements at a Glance',
        },
        {
            type: 'text',
            content:
                '<p><strong>Foundational Four:</strong> <strong>1. Supreme Lord (Paramātmā)</strong>, <strong>2. Time (Kāla)</strong>, <strong>3. Jīva (Soul)</strong>, <strong>4. Prakṛti (Material Nature)</strong>.</p><p class="mt-3"><strong>Internal Four:</strong> <strong>5. Citta</strong>, <strong>6. Ahaṅkāra</strong>, <strong>7. Buddhi</strong>, <strong>8. Manas</strong>.</p><p class="mt-3"><strong>Knowledge Senses (5):</strong> <strong>9. Ear</strong>, <strong>10. Skin</strong>, <strong>11. Eye</strong>, <strong>12. Tongue</strong>, <strong>13. Nose</strong>.</p><p class="mt-3"><strong>Action Senses (5):</strong> <strong>14. Voice</strong>, <strong>15. Hands</strong>, <strong>16. Feet</strong>, <strong>17. Genitals</strong>, <strong>18. Anus</strong>.</p><p class="mt-3"><strong>Subtle Elements (5):</strong> <strong>19. Sound</strong>, <strong>20. Touch</strong>, <strong>21. Form</strong>, <strong>22. Taste</strong>, <strong>23. Smell</strong>.</p><p class="mt-3"><strong>Gross Elements (5):</strong> <strong>24. Ether</strong>, <strong>25. Air</strong>, <strong>26. Fire</strong>, <strong>27. Water</strong>, <strong>28. Earth</strong>.</p>',
        },
        {
            type: 'heading',
            headingText: 'Orientation: How the Count Becomes 28',
        },
        {
            type: 'table',
            table: {
                headers: ['Layer', 'Count', 'Included Items'],
                rows: [
                    ['Products of Prakṛti', '24', '5 gross elements + 5 tan-mātras + 10 senses + 4 internal functions'],
                    ['Additional Foundations', '4', 'Time (kāla), jīva, prakṛti, and the Supreme Lord'],
                    ['Total', '28', 'The full ontology described in Śrīmad-Bhāgavatam, 3rd Canto, 26th Chapter'],
                ],
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'Foundational Cause Layer (Before the 24 Products)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.10',
                sloka: `yat tat tri-guṇam avyaktaṁ\nnityaṁ sad-asad-ātmakam\npradhānaṁ prakṛtiṁ prāhur\naviśeṣaṁ viśeṣavat`,
                translation:
                    'The Lord said: Prakṛti is called avyakta because, though having variety, it has a state of invisibility when the three guṇas are in balance. It is called pradhāna because, manifesting variety, it is the best, being the shelter of all the visible elements. It is called prakṛti because it is the very cause of all causes and effects. It is eternal.',
            },
        },
        {
            type: 'text',
            content:
                'Lord Kapiladeva first defines prakṛti itself before enumerating its products. This is why the chart begins with foundational causes and only then shows the 24 emergent categories.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.15',
                sloka: `etāvān eva saṅkhyāto\nbrahmaṇaḥ sa-guṇasya ha\nsanniveśo mayā prokto\nyaḥ kālaḥ pañca-viṁśakaḥ`,
                translation: 'What I have described as the situation of the Brahman as the material world, of which time is the twenty-fifth element, has thus been enumerated.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.16',
                sloka: `prabhāvaṁ pauruṣaṁ prāhuḥ\nkālam eke yato bhayam\nahaṅkāra-vimūḍhasya\nkartuḥ prakṛtim īyuṣaḥ`,
                translation:
                    'Time is said to be the Lord’s power, by which fear arises in the jīva, having contacted prakrṭi and being bewildered by ego.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.17',
                sloka: `prakṛter guṇa-sāmyasya\nnirviśeṣasya mānavi\nceṣṭā yataḥ sa bhagavān\nkāla ity upalakṣitaḥ`,
                translation:
                    'O Devahūti! Powerful time is described as that which produces agitation in prakṛti, which was previously in a state of equilibrium of the guṇas, without distinctions.',
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'The 24 Products of Material Nature',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.11',
                sloka: `pañcabhiḥ pañcabhir brahma\ncaturbhir daśabhis tathā\netac catur-viṁśatikaṁ\ngaṇaṁ prādhānikaṁ viduḥ`,
                translation: 'The wise know the products of pradhāna, enumerated as five, five, four and ten, to be Brahman.',
            },
        },
        {
            type: 'text',
            content: '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Śrīmad-Bhāgavatam, 3rd Canto, 26th Chapter, Verse 11</div><p class="italic text-black dark:text-gray-100">"The other elements are first enumerated in order to speak of their characteristics. The wise know what is produced from pradhāna (prādhānikam) which are enumerated as five, five, and fourteen to be worshipped as Brahman."</p></div>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.12-14',
                sloka: `mahā-bhūtāni pañcaiva\nbhūr āpo 'gnir marun nabhaḥ\ntan-mātrāṇi ca tāvanti\ngandhādīni matāni me\n\nindriyāṇi daśa śrotraṁ\ntvag dṛg rasana-nāsikāḥ\nvāk karau caraṇau meḍhraṁ\npāyur daśama ucyate\n\nmano buddhir ahaṅkāraś\ncittam ity antar-ātmakam\ncaturdhā lakṣyate bhedo\nvṛttyā lakṣaṇa-rūpayā`,
                translation: 'The five great elements are earth, water, fire, air and ether. The sense objects are also considered to be five, starting with smell. The senses are ten: the ear, skin, eye, tongue, nose, voice, hands, feet, penis and the tenth, the anus. The internal sense organ is divided into four-- the mind, intelligence, ego and citta—because of distinctive functions.',
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'The Four Additional Foundations that Complete the Count',
        },
        {
            type: 'text',
            content: 'Beyond the 24 products of material nature, Lord Kapiladeva describes the four transcendent causes: Time, the Jīva (soul), Prakṛti (unmanifest nature), and the Supreme Lord Himself.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.18',
                sloka: `antaḥ puruṣa-rūpeṇa\nkāla-rūpeṇa yo bahiḥ\nsamanvety eṣa sattvānāṁ\nbhagavān ātma-māyayā`,
                translation: 'He who, by his own energy, exists internally as antaryāmī and externally as time for all beings, is the Supreme Lord.',
            },
        },
        {
            type: 'text',
            content: '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Śrīmad-Bhāgavatam, 3rd Canto, 26th Chapter, Verse 18</div><p class="italic text-black dark:text-gray-100">"Internally the Lord exists without change, continuously as the controller, in the form of antaryāmī. Externally, he exists as the controller in the form of time... In this way there are twenty-four products of prakṛti. Adding time, the jīva, prakṛti and the Lord to this, there are twenty-eight items."</p></div>',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'From Agitation to Manifestation (How the Flow Begins)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.19',
                sloka: `daivāt kṣubhita-dharmiṇyāṁ\nsvasyāṁ yonau paraḥ pumān\nādhatta vīryaṁ sāsūta\nmahat-tattvaṁ hiraṇmayam`,
                translation:
                    'The Supreme Lord placed the jīvas into the womb of prakṛti, whose guṇas were agitated by time. Prakṛti gave birth to the brilliant mahat-tattva.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.26.21',
                sloka: `yat tat sattva-guṇaṁ svacchaṁ\nśāntaṁ bhagavataḥ padam\nyad āhur vāsudevākhyaṁ\ncittaṁ tan mahad-ātmakam`,
                translation:
                    'Mahat-tattva, which is represented by citta in the body, which is predominated by sattva, which is pure, peaceful, and the place to worship the Lord, is also called Vāsudeva.',
            },
        },
        {
            type: 'text',
            content:
                'This is the key transition shown in the diagram: agitation of prakṛti by time leads to mahat-tattva, and mahat-tattva appears in embodied experience as citta. From there, false ego branches into internal faculties, senses, subtle elements, and eventually the gross elements.',
        }
    ]
};

export default content;
